import { useState, useEffect } from 'react';
import { Check, Loader2, CreditCard, AlertTriangle, ExternalLink } from 'lucide-react';
import { billingAPI, PlanConfig } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-hot-toast';

export default function BillingPage() {
  const { user } = useAuth();
  const [plans, setPlans] = useState<PlanConfig[]>([]);
  const [subscription, setSubscription] = useState<any>(null);
  const [usage, setUsage] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [billingInterval, setBillingInterval] = useState<'monthly' | 'yearly'>('monthly');
  const [upgradingPlan, setUpgradingPlan] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [plansRes, subRes] = await Promise.all([
        billingAPI.getPlans(),
        billingAPI.getSubscription(),
      ]);
      setPlans(plansRes.plans);
      setSubscription(subRes.subscription);
      setUsage(subRes.usage);
    } catch (error) {
      console.error('Failed to load billing data:', error);
      toast.error('Failed to load billing information');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpgrade = async (planName: string) => {
    if (planName === user?.plan) return;

    setUpgradingPlan(planName);
    try {
      const { checkout_url } = await billingAPI.createCheckout(planName, billingInterval);
      window.location.href = checkout_url;
    } catch (error) {
      console.error('Failed to create checkout:', error);
      toast.error('Failed to start checkout. Please try again.');
      setUpgradingPlan(null);
    }
  };

  const handleManageBilling = async () => {
    try {
      const { portal_url } = await billingAPI.createPortal();
      window.location.href = portal_url;
    } catch (error) {
      console.error('Failed to open billing portal:', error);
      toast.error('Failed to open billing portal');
    }
  };

  const formatPrice = (cents: number) => {
    return `$${(cents / 100).toFixed(0)}`;
  };

  const getTrialDaysLeft = () => {
    if (!subscription?.trial_ends_at) return null;
    const endDate = new Date(subscription.trial_ends_at);
    const now = new Date();
    const daysLeft = Math.ceil((endDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    return daysLeft > 0 ? daysLeft : 0;
  };

  const trialDaysLeft = getTrialDaysLeft();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 text-primary-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-navy-800">Billing & Plans</h1>
        <p className="mt-1 text-graphite">Manage your subscription and billing</p>
      </div>

      {/* Trial Banner */}
      {trialDaysLeft !== null && trialDaysLeft > 0 && subscription?.status !== 'active' && (
        <div className="bg-gradient-to-r from-aqua-400/20 to-primary-500/20 border border-aqua-400/50 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <AlertTriangle className="h-5 w-5 text-primary-500" />
            <div>
              <p className="font-medium text-navy-800">
                {trialDaysLeft} {trialDaysLeft === 1 ? 'day' : 'days'} left in your free trial
              </p>
              <p className="text-sm text-graphite">
                Subscribe now to continue using all features after your trial ends.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Trial Expired Banner */}
      {trialDaysLeft !== null && trialDaysLeft <= 0 && subscription?.status !== 'active' && user?.plan === 'starter' && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <AlertTriangle className="h-5 w-5 text-red-500" />
            <div>
              <p className="font-medium text-red-800">
                Your free trial has expired
              </p>
              <p className="text-sm text-red-700">
                Subscribe now to continue using ChatbotEmbed. Your widgets are currently paused.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Current Plan */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-navy-800 mb-4">Current Plan</h2>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-2xl font-bold text-navy-800 capitalize">{subscription?.plan || user?.plan}</p>
            <p className="text-sm text-graphite">
              {subscription?.status === 'active' ? (
                <>
                  Active subscription
                  {subscription.current_period_end && (
                    <> - Renews {new Date(subscription.current_period_end).toLocaleDateString()}</>
                  )}
                </>
              ) : subscription?.status === 'canceled' ? (
                'Cancels at end of billing period'
              ) : (subscription?.plan || user?.plan) === 'enterprise' ? (
                'Enterprise account'
              ) : (subscription?.plan || user?.plan) === 'pro' ? (
                'Pro account'
              ) : (
                'Free plan'
              )}
            </p>
          </div>
          {subscription?.stripe_customer_id && (
            <button
              onClick={handleManageBilling}
              className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              <CreditCard className="h-4 w-4" />
              Manage Billing
              <ExternalLink className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* Usage */}
        {usage && (
          <div className="mt-6 pt-6 border-t border-gray-200">
            <h3 className="text-sm font-medium text-graphite mb-3">Current Usage</h3>
            {(() => {
              const messageLimit = subscription?.plan_config?.max_messages_per_month || 1000;
              const messagesSent = usage.messages_sent || 0;
              const usagePercent = Math.min((messagesSent / messageLimit) * 100, 100);
              const isWarning = usagePercent >= 70 && usagePercent < 90;
              const isDanger = usagePercent >= 90;

              return (
                <div className="space-y-4">
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm text-gray-500">Messages this month</p>
                      <p className="text-sm font-medium text-navy-800">
                        {messagesSent.toLocaleString()} of {messageLimit.toLocaleString()}
                      </p>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-3">
                      <div
                        className={`h-3 rounded-full transition-all duration-300 ${
                          isDanger ? 'bg-red-500' : isWarning ? 'bg-yellow-500' : 'bg-green-500'
                        }`}
                        style={{ width: `${usagePercent}%` }}
                      />
                    </div>
                    <div className="flex items-center justify-between mt-1">
                      <p className="text-xs text-gray-400">
                        {usagePercent.toFixed(0)}% used
                      </p>
                      {isDanger && (
                        <p className="text-xs text-red-600 font-medium">
                          {messageLimit - messagesSent <= 0 ? 'Limit reached!' : `${(messageLimit - messagesSent).toLocaleString()} remaining`}
                        </p>
                      )}
                      {isWarning && !isDanger && (
                        <p className="text-xs text-yellow-600 font-medium">
                          {(messageLimit - messagesSent).toLocaleString()} remaining
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
        )}
      </div>

      {/* Billing Interval Toggle */}
      <div className="flex justify-center">
        <div className="bg-gray-100 rounded-lg p-1 inline-flex">
          <button
            onClick={() => setBillingInterval('monthly')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              billingInterval === 'monthly'
                ? 'bg-white text-navy-800 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Monthly
          </button>
          <button
            onClick={() => setBillingInterval('yearly')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              billingInterval === 'yearly'
                ? 'bg-white text-navy-800 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Yearly
            <span className="ml-1 text-xs text-green-600 font-normal">Save 17%</span>
          </button>
        </div>
      </div>

      {/* Plans Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {plans.map((plan) => {
          const isCurrentPlan = plan.plan_name === user?.plan;
          const price = billingInterval === 'yearly' ? plan.price_yearly / 12 : plan.price_monthly;
          const isPopular = plan.plan_name === 'pro';

          return (
            <div
              key={plan.id}
              className={`relative bg-white rounded-xl shadow-sm border-2 p-6 ${
                isPopular ? 'border-primary-500' : 'border-gray-200'
              }`}
            >
              {isPopular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <span className="bg-primary-500 text-white text-xs font-medium px-3 py-1 rounded-full">
                    Most Popular
                  </span>
                </div>
              )}

              <div className="text-center">
                <h3 className="text-lg font-semibold text-navy-800">{plan.display_name}</h3>
                <p className="text-sm text-graphite mt-1">{plan.description}</p>
                <div className="mt-4">
                  {plan.trial_days > 0 && plan.plan_name === 'starter' ? (
                    <>
                      <span className="text-4xl font-bold text-navy-800">Free</span>
                      <span className="text-graphite"> for {plan.trial_days} days</span>
                      <p className="text-sm text-gray-500 mt-1">
                        Then {formatPrice(price)}/mo
                      </p>
                    </>
                  ) : (
                    <>
                      <span className="text-4xl font-bold text-navy-800">{formatPrice(price)}</span>
                      <span className="text-graphite">/mo</span>
                    </>
                  )}
                </div>
                {billingInterval === 'yearly' && plan.price_yearly > 0 && plan.plan_name !== 'starter' && (
                  <p className="text-sm text-gray-500 mt-1">
                    {formatPrice(plan.price_yearly)}/year billed annually
                  </p>
                )}
              </div>

              <ul className="mt-6 space-y-3">
                {plan.features.map((feature, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <Check className="h-5 w-5 text-green-500 flex-shrink-0" />
                    <span className="text-sm text-graphite">{feature}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-6">
                {isCurrentPlan && subscription?.status === 'active' ? (
                  <button
                    disabled
                    className="w-full py-2 px-4 border border-gray-300 rounded-lg text-sm font-medium text-gray-500 bg-gray-50 cursor-not-allowed"
                  >
                    Current Plan
                  </button>
                ) : isCurrentPlan && subscription?.status !== 'active' && plan.price_monthly > 0 ? (
                  <button
                    onClick={() => handleUpgrade(plan.plan_name)}
                    disabled={upgradingPlan === plan.plan_name}
                    className="w-full py-2 px-4 rounded-lg text-sm font-medium transition-colors bg-primary-500 text-white hover:bg-primary-600 disabled:opacity-50"
                  >
                    {upgradingPlan === plan.plan_name ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin inline mr-2" />
                        Processing...
                      </>
                    ) : (
                      'Subscribe Now'
                    )}
                  </button>
                ) : (
                  <button
                    onClick={() => handleUpgrade(plan.plan_name)}
                    disabled={upgradingPlan === plan.plan_name || plan.price_monthly === 0}
                    className={`w-full py-2 px-4 rounded-lg text-sm font-medium transition-colors ${
                      plan.price_monthly === 0
                        ? 'border border-gray-300 text-gray-500 bg-gray-50 cursor-not-allowed'
                        : isPopular
                        ? 'bg-primary-500 text-white hover:bg-primary-600'
                        : 'border border-primary-500 text-primary-500 hover:bg-primary-50'
                    } disabled:opacity-50`}
                  >
                    {upgradingPlan === plan.plan_name ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin inline mr-2" />
                        Processing...
                      </>
                    ) : plan.price_monthly === 0 ? (
                      'Free Trial'
                    ) : (
                      `Upgrade to ${plan.display_name}`
                    )}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* FAQ */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-navy-800 mb-4">Frequently Asked Questions</h2>
        <div className="space-y-4">
          <div>
            <h3 className="font-medium text-navy-800">Can I change my plan later?</h3>
            <p className="text-sm text-graphite mt-1">
              Yes, you can upgrade or downgrade your plan at any time. Changes take effect immediately.
            </p>
          </div>
          <div>
            <h3 className="font-medium text-navy-800">What happens when I upgrade?</h3>
            <p className="text-sm text-graphite mt-1">
              You'll be charged the prorated amount for the remainder of the billing period and gain immediate access to all new features.
            </p>
          </div>
          <div>
            <h3 className="font-medium text-navy-800">Can I cancel anytime?</h3>
            <p className="text-sm text-graphite mt-1">
              Yes, you can cancel your subscription at any time. You'll continue to have access until the end of your billing period.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
